import { getDepotMap } from "./depotMapping";

// Shipping rules:
// >500 RON → first 2 depots FREE, each extra depot costs 22 RON
//   e.g. 3 depots → 22 RON, 4 depots → 44 RON
// 300–499 RON + 1 depot → 11 RON
// 300–499 RON + >1 depot→ 22 RON per depot
// <300 RON              → 22 RON per depot
// COD (ramburs)         → +5 RON flat fee

// Per-supplier minimum order amount, enforced at checkout. Keyed by the raw
// `supplier` DB value (case-sensitive, as stored on products.supplier).
export const MIN_ORDER_BY_SUPPLIER = {
	EUROMASTER: 100,
};

function getFinalPrice(item) {
	const reduced = parseFloat(item.priceReduced);
	const regular = parseFloat(item.price);
	return item.priceReduced != null && reduced < regular ? reduced : regular;
}

export function calculateShipping({ cartItems, paymentMethod, allSuppliers }) {
	const orderSubtotal = cartItems.reduce(
		(sum, item) => sum + getFinalPrice(item) * item.quantity,
		0
	);

	// Group items by supplier, count quantities and subtotal
	const supplierQty = {};
	const supplierSubtotal = {};
	cartItems.forEach((item) => {
		const sup = item.supplier?.trim();
		if (!sup) return;
		supplierQty[sup] = (supplierQty[sup] || 0) + item.quantity;
		supplierSubtotal[sup] = (supplierSubtotal[sup] || 0) + getFinalPrice(item) * item.quantity;
	});

	const uniqueSuppliers = Object.keys(supplierQty);
	const depotCount = uniqueSuppliers.length || 1;
	const depotMap = getDepotMap(allSuppliers);

	// Build depot lines sorted A → B → C
	const depotLines = uniqueSuppliers.length
		? uniqueSuppliers
			.map((supplier) => ({
				supplier,
				label: depotMap[supplier]?.label ?? "Depozit ?",
				slug: depotMap[supplier]?.slug ?? "?",
				qty: supplierQty[supplier],
				subtotal: supplierSubtotal[supplier],
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
			.map((depot, index) => {
				let cost;
				if (orderSubtotal > 500) {
					// First 2 depots are free; each additional depot costs 22 RON
					cost = index < 2 ? 0 : 22;
				} else if (orderSubtotal >= 300 && depotCount === 1) {
					cost = 11;
				} else {
					cost = 22;
				}
				return { ...depot, cost };
			})
		: [{ supplier: null, label: "Depozit A", slug: "a", qty: 0, cost: orderSubtotal >= 300 ? 11 : 22 }];

	const shippingSubtotal = depotLines.reduce((sum, d) => sum + d.cost, 0);
	const codFee = paymentMethod === "ramburs" ? 5 : 0;

	return {
		depotLines,
		codFee,
		totalShipping: shippingSubtotal + codFee,
		isFreeShipping: shippingSubtotal === 0,
		depotCount,
		orderSubtotal,
	};
}

// Returns the 2 depots with the most items — used for smart cart suggestion
export function getTopTwoDepots(depotLines) {
	return [...depotLines].sort((a, b) => b.qty - a.qty).slice(0, 2);
}

// Returns depot lines whose subtotal is below that supplier's minimum order
// amount, e.g. [{ label: "Depozit B", minAmount: 100, subtotal: 62.5 }]
export function getMinOrderViolations(depotLines) {
	return depotLines
		.filter((d) => {
			const min = MIN_ORDER_BY_SUPPLIER[d.supplier];
			return min != null && (d.subtotal ?? 0) < min;
		})
		.map((d) => ({ label: d.label, minAmount: MIN_ORDER_BY_SUPPLIER[d.supplier], subtotal: d.subtotal }));
}
